/**
 * Инлайн-редактор короткого текста: клик по значению открывает поле,
 * Enter и blur сохраняют, Escape отменяет. Валидация длины на клиенте.
 */
import { useRef, useState, type ReactNode } from 'react';
import { Pencil } from 'lucide-react';

interface InlineTextEditorProps {
  value: string;
  onSave: (value: string) => void;
  testId: string;
  inputTestId: string;
  errorTestId: string;
  ariaLabel: string;
  title: string;
  maxLength: number;
  /** Пустое значение запрещено. */
  required?: boolean;
  /** Своя проверка значения (после trim): текст ошибки или null. */
  validate?: (value: string) => string | null;
  /** Что показывать вместо value в режиме просмотра (по умолчанию value). */
  display?: ReactNode;
  emptyHint?: string;
  showPencil?: boolean;
  /** Подпись у карандаша: карандаш виден всегда, а не только при наведении. */
  pencilText?: string;
  viewClassName?: string;
  inputClassName?: string;
  wrapperClassName?: string;
}

const tooLong = (n: number): string => `Не больше ${n} символов`;
const EMPTY_ERROR = 'Значение не может быть пустым';

export function InlineTextEditor({
  value,
  onSave,
  testId,
  inputTestId,
  errorTestId,
  ariaLabel,
  title,
  maxLength,
  required = false,
  validate,
  display,
  emptyHint,
  showPencil = false,
  pencilText,
  viewClassName = '',
  inputClassName = 'h-7 w-full rounded border px-2 text-xs',
  wrapperClassName = '',
}: InlineTextEditorProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [error, setError] = useState<string | null>(null);
  const done = useRef(false);

  const start = (): void => {
    done.current = false;
    setDraft(value);
    setError(null);
    setEditing(true);
  };

  const cancel = (): void => {
    done.current = true;
    setError(null);
    setEditing(false);
  };

  const commit = (): void => {
    if (done.current) return;
    const next = draft.trim();
    if (next.length > maxLength) {
      setError(tooLong(maxLength));
      return;
    }
    if (required && next.length === 0) {
      setError(EMPTY_ERROR);
      return;
    }
    const custom = validate?.(next) ?? null;
    if (custom) {
      setError(custom);
      return;
    }
    done.current = true;
    setError(null);
    setEditing(false);
    if (next !== value.trim()) onSave(next);
  };

  if (!editing) {
    return (
      <button
        type="button"
        data-testid={testId}
        title={title}
        className={`group inline-flex items-center gap-1 text-left hover:underline ${viewClassName}`}
        onClick={start}
      >
        <span>{display ?? (value || emptyHint)}</span>
        {pencilText && (
          <span
            data-testid={`${testId}-pencil`}
            className="ml-1 inline-flex shrink-0 items-center gap-0.5 rounded border border-gray-300 px-1 text-[10px] font-normal leading-4 text-gray-500 group-hover:border-gray-400 group-hover:text-gray-700"
          >
            <Pencil aria-hidden="true" className="h-2.5 w-2.5" />
            {pencilText}
          </span>
        )}
        {showPencil && !pencilText && (
          <Pencil
            aria-hidden="true"
            className="h-3 w-3 shrink-0 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100"
          />
        )}
      </button>
    );
  }

  return (
    <div className={wrapperClassName}>
      <input
        autoFocus
        type="text"
        value={draft}
        aria-label={ariaLabel}
        data-testid={inputTestId}
        className={inputClassName}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          else if (e.key === 'Escape') cancel();
        }}
      />
      {error && (
        <div role="alert" data-testid={errorTestId} className="text-xs text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}
